import React, {
    useEffect,
    useMemo,
    useState,
    useCallback,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    DefaultButton,
    IconButton,
    Image,
    Label, Pivot, PivotItem, Shimmer, Stack, Text,
} from "@fluentui/react";
import {
    queryWorkflowStatus,
    rerunTask,
    rerunTaskNoOverwrite,
    getSmartArtVariants,
} from "src/services/aigc";
import { OnlineWorkflow } from "src/types/workflows";
import style from "./index.module.scss";

export const getQueryParam = (key: string) => new URLSearchParams(window.location.search).get(key) || "";

const JSON_PREVIEW_LINES = 100;

const JsonFileDisplay: React.FC<{ url: string; filename: string; }> = ({ url, filename }) => {
    const [json, setJson] = useState<string | null>(null);
    const [expanded, setExpanded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchJson = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            const text = await response.text();
            // Try to pretty print
            try {
                const obj = JSON.parse(text);
                setJson(JSON.stringify(obj, null, 2));
            } catch {
                setJson(text); // fallback to raw
            }
        } catch (e: any) {
            if (e.name === "TypeError" && e.message.includes("fetch")) {
                setError("CORS error - unable to fetch file directly");
            } else {
                setError(e.message);
            }
        } finally {
            setLoading(false);
        }
    }, [url]);

    useEffect(() => {
        fetchJson();
    }, [fetchJson]);

    if (loading) {
        return <div>{`Loading ${filename}...`}</div>;
    }
    if (error) {
        return (
            <div style={{ color: "red" }}>
                {`Error loading ${filename}: ${error}`}
            </div>
        );
    }
    if (!json) {
        return null;
    }

    const lines = json.split("\n");
    const isLong = lines.length > JSON_PREVIEW_LINES;
    const displayContent = expanded ? json : lines.slice(0, JSON_PREVIEW_LINES).join("\n");

    return (
        <div style={{
            background: "#f5f5f5",
            border: "1px solid #ccc",
            padding: 8,
            margin: "8px 0",
            fontFamily: "monospace",
            fontSize: 12,
            borderRadius: 4,
        }}
        >
            <div style={{
                marginBottom: 8,
                fontWeight: "bold",
                color: "#333",
            }}
            >
                {filename}
            </div>
            <div style={{
                maxHeight: 400,
                overflow: "auto",
                background: "white",
                padding: 8,
                border: "1px solid #ddd",
                borderRadius: 2,
            }}
            >
                <pre style={{
                    margin: 0,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                }}
                >
                    {displayContent}
                </pre>
            </div>
            {isLong && (
                <div style={{ marginTop: 8 }}>
                    <button
                        type="button"
                        onClick={() => setExpanded(!expanded)}
                        style={{
                            padding: "4px 8px",
                            fontSize: 12,
                            background: "#007acc",
                            color: "white",
                            border: "none",
                            borderRadius: 2,
                            cursor: "pointer",
                        }}
                    >
                        {expanded ? "Show less" : `Show more (${lines.length - JSON_PREVIEW_LINES} more lines)`}
                    </button>
                </div>
            )}
        </div>
    );
};

// SmartArt Variants Carousel Component - fetches images from dedicated API
const SmartArtCarousel: React.FC<{ workflowId: string }> = ({ workflowId }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [smartArtImages, setSmartArtImages] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());

    useEffect(() => {
        if (!workflowId) return;

        setLoading(true);
        setError(null);

        getSmartArtVariants({ workflowId })
            .then((res: string[]) => {
                setSmartArtImages(res || []);
                // Preload all images
                (res || []).forEach((url, index) => {
                    const img = new window.Image();
                    img.onload = () => {
                        setLoadedImages((prev) => new Set(prev).add(index));
                    };
                    img.src = url;
                });
            })
            .catch((err) => {
                console.error("Failed to fetch SmartArt variants:", err);
                setError("Failed to load SmartArt variants");
                setSmartArtImages([]);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [workflowId]);

    const handlePrev = () => {
        setCurrentIndex((prev) => (prev > 0 ? prev - 1 : smartArtImages.length - 1));
    };

    const handleNext = () => {
        setCurrentIndex((prev) => (prev < smartArtImages.length - 1 ? prev + 1 : 0));
    };

    if (loading) {
        return (
            <div style={{ padding: "16px 0", marginBottom: 16 }}>
                <Label>SmartArt Variants</Label>
                <Shimmer style={{ marginTop: 8, height: 200 }} />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: "16px 0", marginBottom: 16, color: "#a4262c" }}>
                <Label>SmartArt Variants</Label>
                <div style={{ marginTop: 8 }}>{error}</div>
            </div>
        );
    }

    if (smartArtImages.length === 0) {
        return null; // Don't show section if no images
    }

    return (
        <div style={{ padding: "16px 0", marginBottom: 16 }}>
            <Label>{`SmartArt Variants (${smartArtImages.length} images)`}</Label>
            <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 16,
                marginTop: 8,
            }}
            >
                <IconButton
                    iconProps={{ iconName: "ChevronLeft" }}
                    title="Previous"
                    ariaLabel="Previous image"
                    onClick={handlePrev}
                    disabled={smartArtImages.length <= 1}
                    styles={{
                        root: {
                            width: 40,
                            height: 40,
                            backgroundColor: "#f3f2f1",
                            borderRadius: "50%",
                        },
                        rootHovered: {
                            backgroundColor: "#edebe9",
                        },
                    }}
                />
                <div style={{
                    flex: 1,
                    maxWidth: 800,
                    textAlign: "center",
                    position: "relative",
                    minHeight: 300,
                }}
                >
                    {/* Render all images but only show current one - prevents flash on switch */}
                    <div style={{
                        position: "relative",
                        width: "100%",
                        height: 500,
                        overflow: "hidden",
                    }}
                    >
                        {smartArtImages.map((url, index) => (
                            <img
                                key={url}
                                src={url}
                                alt={`SmartArt variant ${index + 1}`}
                                style={{
                                    position: "absolute",
                                    top: 0,
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    maxWidth: "100%",
                                    maxHeight: 500,
                                    objectFit: "contain",
                                    border: "1px solid #e1e1e1",
                                    borderRadius: 4,
                                    opacity: index === currentIndex ? 1 : 0,
                                    transition: "opacity 0.2s ease-in-out",
                                    pointerEvents: index === currentIndex ? "auto" : "none",
                                }}
                            />
                        ))}
                        {!loadedImages.has(currentIndex) && (
                            <div style={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                            }}
                            >
                                <Shimmer style={{ width: 400, height: 300 }} />
                            </div>
                        )}
                    </div>
                    <div style={{
                        marginTop: 8,
                        fontSize: 14,
                        color: "#605e5c",
                    }}
                    >
                        {`${currentIndex + 1} / ${smartArtImages.length}`}
                    </div>
                    <div style={{
                        marginTop: 4,
                        fontSize: 11,
                        color: "#888",
                        wordBreak: "break-all",
                    }}
                    >
                        {/* Extract filename from URL */}
                        {decodeURIComponent(smartArtImages[currentIndex]?.split("/").pop()?.split("?")[0] || "")}
                    </div>
                </div>
                <IconButton
                    iconProps={{ iconName: "ChevronRight" }}
                    title="Next"
                    ariaLabel="Next image"
                    onClick={handleNext}
                    disabled={smartArtImages.length <= 1}
                    styles={{
                        root: {
                            width: 40,
                            height: 40,
                            backgroundColor: "#f3f2f1",
                            borderRadius: "50%",
                        },
                        rootHovered: {
                            backgroundColor: "#edebe9",
                        },
                    }}
                />
            </div>
        </div>
    );
};

export const WorkflowDetail: React.FC = () => {
    const location = useLocation();
    const history = useNavigate();
    const [selectedKey, setSelectedKey] = React.useState("");
    const [loading, setLoading] = useState<boolean>(false);
    const [data, setData] = useState<OnlineWorkflow>();
    const task = useMemo(() => data?.tasks?.[Number(selectedKey)], [data, selectedKey]);

    const handleLinkClick = (item?: PivotItem) => {
        if (item) {
            setSelectedKey(item.props.itemKey as string);
        }
    };

    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    const handleRerun = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();

        const workflowId = getQueryParam("workflowId");
        if (!workflowId) {
            alert("Workflow ID is missing.");
            return;
        }

        const taskName = task?.name ?? "";
        if (!taskName) {
            alert("Task name is missing.");
            return;
        }

        if (!window.confirm(
            `The data of task ${taskName} and following tasks will be deleted! \n`
            + `Do you want to rerun ${taskName} task for workflow ${workflowId}?`,
        )) return;

        setIsButtonDisabled(true);
        setLoading(true);
        try {
            rerunTask({ workflowId, taskName })
                .then((res) => {
                    console.log(res);
                    alert(`Task ${taskName} is rerunning.`);
                    window.location.reload();
                })
                .catch((err) => {
                    if (err.response) {
                        console.log(err.response.data);
                        console.log(err.response.status);
                        console.log(err.response.headers);
                        alert(err.response.data);
                    } else {
                        alert(err);
                    }
                })
                .finally(() => {
                    setIsButtonDisabled(false);
                    setLoading(false);
                });
        } catch {
            setIsButtonDisabled(false);
            setLoading(false);
        }
    };

    const handleRerunNoOverwrite = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();

        const workflowId = getQueryParam("workflowId");
        if (!workflowId) {
            alert("Workflow ID is missing.");
            return;
        }

        const taskName = task?.name ?? "";
        if (!taskName) {
            alert("Task name is missing.");
            return;
        }

        if (!window.confirm(
            "Rerun the task without re-generating existing data! \n"
            + `Do you want to rerun ${taskName} task for workflow ${workflowId}?`,
        )) return;

        setIsButtonDisabled(true);
        setLoading(true);
        try {
            rerunTaskNoOverwrite({ workflowId, taskName })
                .then((res) => {
                    console.log(res);
                    alert(`Task ${taskName} is rerunning.`);
                    window.location.reload();
                })
                .catch((err) => {
                    if (err.response) {
                        console.log(err.response.data);
                        console.log(err.response.status);
                        console.log(err.response.headers);
                        alert(err.response.data);
                    } else {
                        alert(err);
                    }
                })
                .finally(() => {
                    setIsButtonDisabled(false);
                    setLoading(false);
                });
        } catch {
            setIsButtonDisabled(false);
            setLoading(false);
        }
    };

    useEffect(() => {
        setLoading(true);
        const queryData = () => queryWorkflowStatus({ workflowId: getQueryParam("workflowId") })
            .then((res) => {
                if (res) setData(res);
            })
            .catch((err) => {
                alert(err);
            })
            .finally(() => setLoading(false));
        queryData();
        const interval = setInterval(queryData, 20000);
        return () => clearInterval(interval);
    }, [location.search]);
    return (
        <div className={style.workflowDetail}>
            <h3 className={style.title}>
                <DefaultButton text="Back" iconProps={{ iconName: "Back" }} onClick={() => history("/workflow")} />
                <span>WorkflowDetail</span>
                <span style={{ minWidth: "100px" }}>{data?.status}</span>
            </h3>
            <hr />
            <div>
                <Stack tokens={{ childrenGap: 50 }}>
                    {/* Overview Section */}
                    <hr className={style.highlightSeparator} />
                    <div className={style.sectionHeader}>Overview</div>
                    <Stack horizontal tokens={{ childrenGap: 26, padding: "0 12px" }} wrap>
                        <div className={style.item}>
                            <Label>SessionId:</Label>
                            <Text>{data?.sessionId || !loading || <Shimmer />}</Text>
                        </div>
                        <div className={style.item}>
                            <Label>WorkflowId:</Label>
                            <Text>{data?.workflowId || !loading || <Shimmer />}</Text>
                        </div>
                        <div className={style.item}>
                            <Label>workflowTemplateName:</Label>
                            <Text>{data?.workflowTemplateName || !loading || <Shimmer />}</Text>
                        </div>
                        <div className={style.item}>
                            <Label>status:</Label>
                            <Text>{data?.status || !loading || <Shimmer />}</Text>
                        </div>
                        <div className={style.item}>
                            <Label>timestamp (UTC):</Label>
                            <Text>{data?.timestamp || !loading || <Shimmer />}</Text>
                        </div>
                    </Stack>
                    {/* Input Section */}
                    <hr className={style.highlightSeparator} />
                    <div className={style.sectionHeader}>Running Details</div>
                    <Stack horizontal tokens={{ childrenGap: 26, padding: "0 12px" }} wrap>
                        <div className={style.inputItem}>
                            <Label>topic</Label>
                            <Text>{data?.input?.topic || !loading || <Shimmer />}</Text>
                        </div>
                        <div className={style.inputItem}>
                            <Label>startTimestamp</Label>
                            <Text>
                                {data?.startTimestamp
                                    ? new Date(data.startTimestamp).toLocaleString("zh-CN", {
                                        year: "numeric",
                                        month: "2-digit",
                                        day: "2-digit",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        second: "2-digit",
                                    })
                                    : !loading || <Shimmer />}
                            </Text>
                        </div>
                        <div className={style.inputItem}>
                            <Label>endTimestamp</Label>
                            <Text>
                                {data?.endTimestamp
                                    ? new Date(data.endTimestamp).toLocaleString("zh-CN", {
                                        year: "numeric",
                                        month: "2-digit",
                                        day: "2-digit",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        second: "2-digit",
                                    })
                                    : !loading || <Shimmer />}
                            </Text>
                        </div>
                        <div className={style.inputItem}>
                            <Label>runDuration (s)</Label>
                            <Text>
                                {data?.runDuration
                                    ? `${Number(data.runDuration).toFixed(1)}s`
                                    : !loading || <Shimmer />}
                            </Text>
                        </div>
                        <div className={style.inputItem}>
                            <Label>version</Label>
                            <Text>{data?.version || !loading || <Shimmer />}</Text>
                        </div>
                        <div className={style.inputItem}>
                            <Label>toggleSettings</Label>
                            <Text style={{ whiteSpace: "pre-wrap", fontFamily: "monospace", fontSize: "12px" }}>
                                {data?.toggleSettings
                                    ? (() => {
                                        try {
                                            // 如果已经是对象,直接序列化
                                            if (typeof data.toggleSettings === "object") {
                                                return JSON.stringify(data.toggleSettings, null, 2);
                                            }
                                            // 如果是字符串,检查是否为C#类型名
                                            if (typeof data.toggleSettings === "string") {
                                                if (data.toggleSettings.startsWith("System.")
                                                    || data.toggleSettings.includes("JsonSerializerOptions")) {
                                                    return "N/A (Invalid data format)";
                                                }
                                                // 尝试解析JSON字符串
                                                return JSON.stringify(JSON.parse(data.toggleSettings), null, 2);
                                            }
                                            return String(data.toggleSettings);
                                        } catch (e) {
                                            // 解析失败,显示原始值或N/A
                                            return typeof data.toggleSettings === "string"
                                                ? data.toggleSettings
                                                : "N/A (Invalid format)";
                                        }
                                    })()
                                    : !loading || <Shimmer />}
                            </Text>
                        </div>
                    </Stack>
                    {/* Tasks Section */}
                    <hr className={style.highlightSeparator} />
                    <div className={style.sectionHeader}>Tasks</div>
                    <Pivot
                        onLinkClick={handleLinkClick}
                        selectedKey={selectedKey}
                        headersOnly
                    >
                        {
                            data?.tasks?.map((task: { name: string }, index: number) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <PivotItem headerText={task.name} itemKey={String(index)} key={`${task.name}-${index}`} />
                            ))
                        }
                    </Pivot>
                    <Stack tokens={{ childrenGap: 32, padding: "16px 12px" }}>
                        <Stack horizontal tokens={{ childrenGap: 16 }}>
                            <DefaultButton
                                text="Rerun Task"
                                type="button"
                                disabled={isButtonDisabled}
                                onClick={(e) => handleRerun(e as React.MouseEvent<HTMLButtonElement>)}
                            />
                            <DefaultButton
                                text="Rerun Task (No Regenerate Existing Data)"
                                type="button"
                                disabled={isButtonDisabled}
                                onClick={(e) => handleRerunNoOverwrite(e as React.MouseEvent<HTMLButtonElement>)}
                            />
                        </Stack>
                        <div className={style.taskItem}>
                            <Label>name</Label>
                            <Text>{task?.name || !loading || <Shimmer />}</Text>
                        </div>
                        <div className={style.taskItem}>
                            <Label>standardErrorText</Label>
                            <Text>{task?.standardErrorText || !loading || <Shimmer />}</Text>
                        </div>
                        <div className={style.taskItem}>
                            <Label>status</Label>
                            <Text>{task?.status || !loading || <Shimmer />}</Text>
                        </div>
                        <Label>outputFiles</Label>
                        <Stack tokens={{ childrenGap: 26 }} wrap>
                            {
                                task?.outputFiles && Object.keys(task?.outputFiles).map((file) => {
                                    const fileType = file.split(".").at(-1);
                                    const filePath = task.outputFiles[file];
                                    if (fileType === "jpg" || fileType === "png") {
                                        return (
                                            <div
                                                key={`${task.name}-${file}`}
                                                style={{
                                                    marginBottom: 16,
                                                    overflow: "hidden",
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        marginBottom: 8,
                                                        fontWeight: "bold",
                                                        color: "#333",
                                                        fontFamily: "monospace",
                                                        fontSize: 12,
                                                    }}
                                                >
                                                    {file}
                                                </div>
                                                <Image
                                                    style={{
                                                        margin: "0",
                                                        maxWidth: "100%",
                                                        maxHeight: "600px",
                                                        objectFit: "contain",
                                                    }}
                                                    src={filePath}
                                                />
                                            </div>
                                        );
                                    }
                                    if (file.endsWith(".json")) {
                                        return (
                                            <JsonFileDisplay
                                                key={`${task.name}-${file}`}
                                                url={filePath}
                                                filename={file}
                                            />
                                        );
                                    }
                                    if (file.endsWith(".pptx")) {
                                        return (
                                            <div
                                                key={`${task.name}-${file}`}
                                                style={{
                                                    marginBottom: 16,
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        marginBottom: 8,
                                                        fontWeight: "bold",
                                                        color: "#333",
                                                        fontFamily: "monospace",
                                                        fontSize: 12,
                                                    }}
                                                >
                                                    {file}
                                                </div>
                                                <iframe
                                                    src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(filePath)}`}
                                                    width="800"
                                                    height="450"
                                                    frameBorder="0"
                                                    title={file}
                                                    style={{
                                                        borderRadius: 8,
                                                        border: "1px solid #ccc",
                                                        marginBottom: 8,
                                                        maxWidth: "100%",
                                                    }}
                                                />
                                                <div>
                                                    <a
                                                        href={filePath}
                                                        download={file}
                                                        style={{
                                                            display: "inline-block",
                                                            padding: "6px 16px",
                                                            background: "#0078d4",
                                                            color: "white",
                                                            borderRadius: 4,
                                                            textDecoration: "none",
                                                            fontWeight: 500,
                                                            fontSize: 14,
                                                        }}
                                                    >
                                                        Download PPTX
                                                    </a>
                                                </div>
                                            </div>
                                        );
                                    }
                                    return (
                                        <div
                                            key={`${task.name}-${file}`}
                                            style={{
                                                wordBreak: "break-all",
                                            }}
                                        >
                                            <a
                                                aria-label={file}
                                                href={filePath}
                                            >
                                                {file}
                                            </a>
                                        </div>
                                    );
                                })
                            }
                        </Stack>
                        <div className={style.taskItem}>
                            <Label>totalTimeMilliseconds</Label>
                            <Text>
                                {task?.statistics?.totalTimeMilliseconds?.toString() || !loading || <Shimmer />}
                            </Text>
                        </div>
                        <Stack tokens={{ childrenGap: 25 }}>
                            <Label>openai usages</Label>
                            <table>
                                <thead>
                                    <tr>
                                        <th>Model Name</th>
                                        <th>Completion Tokens</th>
                                        <th>Prompt Tokens</th>
                                        <th>Total Tokens</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {task?.statistics?.openAIUsage?.modelUsages
                                    && task?.statistics?.openAIUsage?.modelUsages.map((usage) => (
                                        <tr key={usage.modelName}>
                                            <td>{usage.modelName}</td>
                                            <td>{usage.completionTokens.toString()}</td>
                                            <td>{usage.promptTokens.toString()}</td>
                                            <td>{usage.totalTokens.toString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Stack>
                        {/* SmartArt Variants Carousel */}
                        <SmartArtCarousel workflowId={getQueryParam("workflowId")} />
                        <Stack tokens={{ childrenGap: 25 }}>
                            <Label>logMessages</Label>
                            {
                                task?.logMessages && task.logMessages.length > 0 ? (
                                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                        {/* eslint-disable react/no-array-index-key */}
                                        {task.logMessages.map((log, idx) => (
                                            <div
                                                key={`${task.name}-log-${idx}`}
                                                style={{
                                                    background: "#f8f8fa",
                                                    border: "1px solid #e0e0e0",
                                                    borderRadius: 4,
                                                    padding: "8px 12px",
                                                    fontFamily: "monospace",
                                                    fontSize: 13,
                                                    color: "#222",
                                                    maxHeight: 120,
                                                    overflow: "auto",
                                                    whiteSpace: "pre-wrap",
                                                    wordBreak: "break-word",
                                                }}
                                            >
                                                {log}
                                            </div>
                                        ))}
                                        {/* eslint-enable react/no-array-index-key */}
                                    </div>
                                ) : (
                                    <span style={{ color: "#888", fontStyle: "italic" }}>No logs available.</span>
                                )
                            }
                        </Stack>
                    </Stack>
                </Stack>
            </div>
        </div>
    );
};
